import React from 'react';
import classNames from 'classnames';
import { Box, Button, CircularProgress, Fade } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import { CustomIcon, Translation } from '../index';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    flexShrink: 0,
    textTransform: 'none',
    transition: `background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    width 200ms linear,
    min-width 200ms linear,
    padding 200ms linear`,

    '&:disabled': {
      background: theme.palette.text.disabled,
      color: theme.palette.primary.contrastText
    }
  },
  rootLoading: {
    pointerEvents: 'none',
    minWidth: '40px !important',
    width: '40px',
    padding: '0px !important'
  },
  body1Bold: {
    ...theme.typography.body1Bold
  },
  subtitle1: {
    ...theme.typography.subtitle1
  },
  subtitle3: {
    ...theme.typography.subtitle3
  },
  largeContained: {
    minWidth: 174,
    height: 40,
    padding: '0 24px',
    borderRadius: 24
  },
  medium: {
    padding: '0 8px',
    height: 32,
    borderRadius: 8
  },
  mediumContained: {
    padding: '0 28px',
    height: 32,
    borderRadius: 16
  },
  largeOutlined: {
    minWidth: 174,
    height: 40,
    padding: '0 23px',
    borderRadius: 24
  },
  mediumOutlined: {
    padding: '0 27px',
    height: 32,
    borderRadius: 16
  },
  primaryContained: {
    '&:hover': {
      background: theme.palette.primary.light
    }
  },
  secondaryContained: {
    '&:hover': {
      background: theme.palette.secondary.light
    }
  },
  secondaryOutlined: {
    borderColor: theme.palette.secondary.main
  },
  icon: {
    marginLeft: 0
  },
  label: {
    '@supports (-webkit-hyphens:none)': {
      '& > span:last-of-type': {
        marginBottom: -1
      }
    }
  },
  loader: {
    display: 'flex',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',

    '& circle': {
      stroke: theme.palette.primary.dark
    }
  },
  '@keyframes textIn': {
    '0%': {
      position: 'absolute',
      overflow: 'visible',
      opacity: 0,
      whiteSpace: 'nowrap'
    },
    '99%': {
      position: 'absolute',
      overflow: 'visible',
      whiteSpace: 'nowrap'
    },
    '100%': {
      position: 'static',
      opacity: 1
    }
  },
  '@keyframes textOut': {
    from: {
      position: 'absolute',
      whiteSpace: 'nowrap',
      overflow: 'visible',
      opacity: 1
    },
    to: {
      position: 'absolute',
      whiteSpace: 'nowrap',
      overflow: 'visible',
      opacity: 0
    }
  },
  textAnimateIn: {
    animation: '$textIn',
    animationDuration: '200ms',
    animationIterationCount: '1',
    animationFillMode: 'forwards'
  },
  textAnimateOut: {
    animation: '$textOut',
    animationDuration: '200ms',
    animationIterationCount: '1',
    animationFillMode: 'forwards'
  }
}));

const ThemeButton = ({
  className,
  id,
  component,
  textKey,
  color = 'primary',
  size = 'large',
  variant,
  fontStyle = 'body1Bold',
  onClick,
  type = 'button',
  startIcon,
  startIconColor,
  startElement,
  disabled,
  hasAnimation,
  isLoading
}) => {
  const classes = useStyles();

  let variantCapitalized = '';
  if (variant) {
    variantCapitalized = variant.charAt(0).toUpperCase() + variant.slice(1);
  }

  const colorClass = `${color}${variantCapitalized}`;
  const sizeClass = `${size}${variantCapitalized}`;

  return (
    <Button
      disableElevation
      variant={variant}
      id={id}
      classes={{
        root: classNames(
          classes.root,
          classes[fontStyle],
          classes[sizeClass],
          classes[colorClass],
          className,
          isLoading && classes.rootLoading
        ),
        label: classes.label,
        startIcon: classes.icon
      }}
      color={color}
      component={component}
      onClick={isLoading ? undefined : onClick}
      type={type}
      startIcon={
        startIcon ? (
          <CustomIcon
            className={classes.icon}
            icon={startIcon}
            variant={startIconColor}
            size="xs"
          />
        ) : (
          startElement
        )
      }
      disabled={disabled}
    >
      <Translation
        keyIfNoValue
        inline
        noWrap
        className={classNames(
          hasAnimation && !isLoading && classes.textAnimateIn,
          hasAnimation && isLoading && classes.textAnimateOut
        )}
      >
        {textKey}
      </Translation>
      <Fade in={isLoading} timeout={{ enter: 200, exit: 100 }} unmountOnExit>
        <Box className={classes.loader}>
          <CircularProgress size={40} />
        </Box>
      </Fade>
    </Button>
  );
};

export default ThemeButton;
